import { env } from "@/web/lib/env";

/**
 * Base64 alphabet, padding excluded — we strip padding before testing (see
 * below) so this only has to accept the payload characters.
 *
 * Once the input is unpadded, the only two things `atob` still rejects are a
 * character outside this alphabet and a length that leaves a lone trailing
 * sextet (`length % 4 === 1`). Screening for both is what lets this function
 * decode without a `try`/`catch` — which matters because it runs during SSR,
 * where the catch handler our lint rule would require (`ErrorHandler`) pulls a
 * `"use client"` logging module into the server bundle.
 */
const BASE64 = /^[A-Za-z0-9+/]+$/;

/**
 * Origin that `clerk.browser.js` is fetched from, for a `preconnect` hint.
 *
 * Clerk encodes the frontend API host inside the publishable key itself
 * (`pk_<test|live>_<base64>`, decoding to `<host>$`), and `@clerk/react` does
 * not re-export its parser — so we decode it here. It MUST NOT be hardcoded:
 * it is `clerk.with.co` in prod but `<slug>.clerk.accounts.dev` on the dev
 * instance, and a wrong hint would warm a connection nobody uses.
 *
 * Returns null on anything unexpected. This only feeds a performance hint, so
 * an unrecognised key shape should degrade to "no hint" rather than break the
 * document it is rendered into.
 */
export function clerkFrontendApiOrigin(): string | null {
  // `pk_<instance>_<base64>` — the third segment is the encoded host. Clerk
  // emits it unpadded, but strip any stray `=` up front: `atob` throws on
  // padding it cannot place (e.g. `abcd==`, which clears the length and
  // alphabet checks below), and this function must return null rather than
  // throw during the SSR document render it feeds.
  const encoded = env.VITE_CLERK_PUBLISHABLE_KEY.split("_")[2]?.replace(/=+$/, "");
  if (!encoded || encoded.length % 4 === 1 || !BASE64.test(encoded)) return null;

  // `atob` rather than `Buffer`: this also runs in the Cloudflare worker.
  const host = atob(encoded).replace(/\$+$/, "");
  // Keys are unpadded base64, so a truncated one can still decode to garbage.
  // A host we would put in a `preconnect` must at least look like a hostname.
  return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(host) ? `https://${host}` : null;
}
